import { Layout } from "providers/Layout";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { useProgressContext } from "providers/ProgressContext";
import { Button, ButtonVariant, Checkbox, Modal } from "shared/components";
import {
  TimeLine,
  TimeLineItem
} from "../rent-reporting-upload/components/TimeLine";

import { useEffect, useMemo, useState } from "react";
import { Check, InfoCircle } from "untitledui-js-base";
import { instance } from "api/apiBase";
import { useMutation } from "@tanstack/react-query";
import { getErrorMessage } from "utils/error";

import { Banner } from "../rent-reporting-upload/components/Banner";
import { cx } from "shared/utils";
import { ListBox } from "../rent-reporting-upload/components/ListBox";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { Card } from "../rent-reporting-upload/components/Card";
import { useAuthContext } from "providers/Auth";
import { toast } from "react-toastify";
import { Redirect } from "shared/components/Redirect";
import { CsvUploadStatus } from "providers/ProgressContext/slices/UploadCsvSlice/types";
import { analysisCrumbs } from "./constants";
import { getEnrollmentsByStateCopy } from "views/renters/utils";

export const RentReportingCsvAnalysisView = () => {
  const { type, partnerDefaultEnrollments, permissions } = useAuthContext();
  const [cancelModal, setCancelModal] = useState(false);
  const { uploadCsv } = useProgressContext();

  const [formData, setFormData] = useState({
    verify: false,
    enroll: false,
    resubscribe: false,
    unenroll: false,
    update: false,
    moveout: false,
    opt_in: false,
    opt_out: false,
    is_default_enrollment: false
  });

  const defaultEnrollmentSubText = useMemo(
    () =>
      getEnrollmentsByStateCopy(
        uploadCsv.meta?.data?.enrollments_by_state || {},
        partnerDefaultEnrollments?.default_enrollment_type,
        partnerDefaultEnrollments?.state_province_defaults || []
      ),
    [uploadCsv.meta?.data?.enrollments_by_state, partnerDefaultEnrollments]
  );

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      is_default_enrollment: !!defaultEnrollmentSubText.length
    }));
  }, [defaultEnrollmentSubText]);

  const { mutate: handleSubmit, isPending } = useMutation({
    mutationKey: ["UPLOAD_CSV", uploadCsv.meta?.data?.id],
    mutationFn: () => {
      return instance
        .post(`portal/v1/csv/${uploadCsv.meta?.data?.id}/process`, formData)
        .then(() => {
          return uploadCsv.meta?.refetch();
        });
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  // TODO: add confirmation modal before cancel
  const { mutate: handleCancel, isPending: isCancelPending } = useMutation({
    mutationKey: ["UPLOAD_CSV_CANCEL", uploadCsv.meta?.data?.id],
    mutationFn: () => {
      return instance
        .post(`portal/v1/csv/${uploadCsv.meta?.data?.id}/cancel`)
        .then(() => {
          return uploadCsv.meta?.refetch();
        });
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  const actionsAvailable = useMemo(() => {
    const stats = uploadCsv.meta?.data?.stats;
    if (!stats) return false;

    return (
      stats.enroll > 0 ||
      stats.resubscribe > 0 ||
      stats.unenroll > 0 ||
      stats.verify > 0 ||
      stats.data_update > 0 ||
      stats.move_out > 0
    );
  }, [uploadCsv.meta?.data?.stats]);

  // Should not appear. This is mostly to satisfy ts further on
  if (!uploadCsv.meta?.data) return null;
  const data = uploadCsv.meta.data;

  if (type.isPMS && !type.isBoomOps && !type.canManageEntitiesDirectly)
    return null;
  if (
    data.status !== CsvUploadStatus.Parsed ||
    !permissions.canSubscribeRenters
  )
    return <Redirect href="/" />;

  const hasValid = data.stats.valid_count;
  const hasInvalid = data.stats.invalid_count;

  const canSubmit =
    !isPending &&
    (formData.enroll ||
      formData.unenroll ||
      formData.resubscribe ||
      formData.verify ||
      formData.update ||
      formData.moveout);

  return (
    <Layout
      headerClassName="pt-3"
      header={
        <div className="border-b border-alpha-100">
          <div className="mb-3 ml-6">
            <BreadCrumbs crumbs={analysisCrumbs} />
          </div>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          {!hasValid ? (
            <Button
              className="w-[125px]"
              variant={ButtonVariant.PRIMARY}
              onClick={() => {
                !isCancelPending && setCancelModal(true);
              }}
              loading={isCancelPending}
              disabled={isCancelPending}
            >
              Go back
            </Button>
          ) : !hasInvalid ? (
            <>
              <Button
                className="w-[125px]"
                onClick={() => {
                  !isCancelPending && setCancelModal(true);
                }}
                loading={isCancelPending}
                disabled={isCancelPending}
              >
                Cancel
              </Button>
              <Button
                className="w-[125px]"
                variant={ButtonVariant.PRIMARY}
                loading={isPending}
                disabled={!canSubmit || isPending}
                onClick={() => {
                  canSubmit && handleSubmit();
                }}
              >
                Process
              </Button>
            </>
          ) : (
            <>
              <Button
                className="w-[125px]"
                onClick={() => {
                  !isCancelPending && setCancelModal(true);
                }}
                loading={isCancelPending}
                disabled={isCancelPending}
              >
                Cancel
              </Button>
              <Button
                className="w-[160px]"
                variant={ButtonVariant.PRIMARY}
                loading={isPending}
                disabled={!canSubmit || isPending}
                onClick={() => {
                  canSubmit && handleSubmit();
                }}
              >
                Process valid data
              </Button>
            </>
          )}
        </div>
      }
    >
      <Banner />
      <div className="animate-appear mx-auto max-w-[760px]">
        <Card className="h-full p-8">
          <div className="border-b border-alpha-40 pb-6">
            <h1 className="mb-1 text-xl font-bold text-content-1000">
              CSV file analysis
            </h1>

            <p className="text-base font-medium text-content-400">
              {data.csv_filename || "renters_csv.csv"}
            </p>
          </div>

          <div className="flex h-full gap-8">
            {data.stats.invalid_count ? (
              <div className="grow-1 max-w-1/2 mt-8 w-full outline-offset-2">
                <div className="flex items-center ">
                  <div className="h-5 w-5 -translate-x-px select-none rounded-full bg-danger-500 text-center text-sm font-bold text-white">
                    !
                  </div>

                  <div className="flex w-full justify-between pl-2">
                    <h3 className="text-base font-bold text-content-700">
                      Invalid rows
                    </h3>

                    <p className="text-sm text-danger-600 rounded-xl px-2.5 py-0.5 bg-danger-50 font-medium">
                      {data.stats.invalid_count}
                    </p>
                  </div>
                </div>
                <TimeLine>
                  {Object.entries(data.stats.itemized_errors || {}).map(
                    ([key, value], index) => (
                      <TimeLineItem
                        key={key}
                        isLast={
                          index ===
                          Object.keys(data.stats.itemized_errors || {}).length -
                            1
                        }
                        wrapperClassName="my-5 items-center"
                        childrenClassName="items-center flex w-full text-sm justify-between font-medium gap-2"
                      >
                        <Tooltip>
                          <TooltipTrigger>
                            <span className="block text-content-1000 text-left">
                              {key}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-[300px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-white">
                            <p className="font-bold">{key}</p>
                            <p>{value.description}</p>
                          </TooltipContent>
                          <span className="block text-content-300">
                            {value.count}
                          </span>
                        </Tooltip>
                      </TimeLineItem>
                    )
                  )}
                </TimeLine>
              </div>
            ) : null}
            {actionsAvailable ? (
              <>
                <div className="min-h-full border-l border-alpha-40"> </div>
                <div className="grow-1 max-w-1/2 mt-8 w-full outline-offset-2">
                  <div className="flex items-center ">
                    <div className="flex h-5 w-5 -translate-x-px select-none items-center justify-center rounded-full bg-success-500 text-center text-sm font-bold text-white">
                      <Check
                        className="stroke-white stroke-[2px]"
                        size="14px"
                      />
                    </div>
                    <h3 className="text-base font-bold text-content-700 pl-2">
                      Actions
                    </h3>
                  </div>
                  <TimeLine>
                    {type.isManual ||
                    (type.isPMS && type.isBoomOps) ||
                    (type.isPMS && type.canManageEntitiesDirectly) ? (
                      <>
                        <TimeLineItem
                          wrapperClassName="my-5 items-start"
                          childrenClassName="w-full text-sm font-medium gap-2"
                          bullet={
                            <Checkbox
                              checked={formData.enroll}
                              onChange={(e) => {
                                setFormData((prev) => ({
                                  ...prev,
                                  enroll: e.target.checked
                                }));
                              }}
                              // check designs of disabled state
                              disabled={data.stats.enroll === 0}
                              wrapperClassName="!w-auto translate-x-[-19px]"
                            />
                          }
                        >
                          <div
                            className={cx(
                              "mb-2 flex items-center",
                              data.stats.enroll === 0
                                ? "text-content-100"
                                : "text-content-1000"
                            )}
                          >
                            <Tooltip>
                              <TooltipTrigger>
                                <span className="-ml-1.5 text-sm text-inherit">
                                  Subscribe renters
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-[300px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-white">
                                <p className="font-bold">Subscribe renters</p>
                                <p>
                                  You can specify whether you want to subscribe
                                  renters who are listed in the file
                                </p>
                              </TooltipContent>
                            </Tooltip>
                            <span
                              className={cx(
                                "ml-auto block text-right",
                                data.stats.enroll === 0
                                  ? "text-content-100"
                                  : "text-content-300"
                              )}
                            >
                              {data.stats.enroll}
                            </span>
                          </div>
                          <ListBox
                            value={{
                              opt_in: formData.opt_in,
                              opt_out: formData.opt_out,
                              is_default_enrollment:
                                formData.is_default_enrollment
                            }}
                            disabled={
                              data.stats.enroll === 0 || !formData.enroll
                            }
                            setValue={(value) => {
                              setFormData((prev) => ({
                                ...prev,
                                ...value
                              }));
                            }}
                            defaultEnrollmentSubText={defaultEnrollmentSubText}
                          />
                        </TimeLineItem>

                        <TimeLineItem
                          wrapperClassName="my-5 items-center"
                          childrenClassName="items-center flex w-full text-sm justify-between font-medium gap-2"
                          bullet={
                            <Tooltip>
                              <TooltipTrigger>
                                <Checkbox
                                  checked={formData.resubscribe}
                                  onChange={(e) => {
                                    setFormData((prev) => ({
                                      ...prev,
                                      resubscribe: e.target.checked
                                    }));
                                  }}
                                  disabled={data.stats.resubscribe === 0}
                                  label="Resubscribe renters"
                                  wrapperClassName="!w-auto translate-x-[-19px]"
                                />
                              </TooltipTrigger>
                              <TooltipContent className="max-w-[300px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-white">
                                <p className="font-bold">Resubscribe renters</p>
                                <p>
                                  You can specify whether you want to
                                  resubscribe renters who are listed in the file
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          }
                        >
                          <span
                            className={cx(
                              "ml-auto block",
                              data.stats.resubscribe === 0
                                ? "text-content-100"
                                : "text-content-300"
                            )}
                          >
                            {data.stats.resubscribe}
                          </span>
                        </TimeLineItem>
                      </>
                    ) : null}

                    <TimeLineItem
                      wrapperClassName="my-5 items-center"
                      childrenClassName="items-center flex w-full text-sm justify-between font-medium gap-2"
                      bullet={
                        <Tooltip>
                          <TooltipTrigger>
                            <Checkbox
                              checked={formData.unenroll}
                              onChange={(e) => {
                                setFormData((prev) => ({
                                  ...prev,
                                  unenroll: e.target.checked
                                }));
                              }}
                              disabled={data.stats.unenroll === 0}
                              label="Unsubscribe renters"
                              wrapperClassName="!w-auto translate-x-[-19px]"
                            />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-[300px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-white">
                            <p className="font-bold">Unsubscribe renters</p>
                            <p>
                              You can specify whether you want to unsubscribe
                              renters who are missing in the file
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      }
                    >
                      <span
                        className={cx(
                          "ml-auto block",
                          data.stats.unenroll === 0
                            ? "text-content-100"
                            : "text-content-300"
                        )}
                      >
                        {data.stats.unenroll}
                      </span>
                    </TimeLineItem>

                    <TimeLineItem
                      wrapperClassName="my-5 items-center"
                      childrenClassName="items-center flex w-full text-sm justify-between font-medium gap-2"
                      bullet={
                        <Tooltip>
                          <TooltipTrigger>
                            <Checkbox
                              checked={formData.verify}
                              disabled={Boolean(data.stats.verify === 0)}
                              onChange={(e) => {
                                setFormData((prev) => ({
                                  ...prev,
                                  verify: e.target.checked
                                }));
                              }}
                              label="Verify payments"
                              wrapperClassName="!w-auto translate-x-[-19px]"
                            />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-[300px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-white">
                            <p className="font-bold">Verify payments</p>
                            <p>
                              You can specify whether you want report rent for
                              renters listed in the file
                            </p>

                            <ul className="list-disc pl-4 mt-4">
                              <li className="mb-2">
                                Outstanding balance will be used to verify rent
                                payments
                              </li>
                              <li className="mb-2">
                                Past months will be reported as &apos;No
                                Data&apos; if you don&apos;t report rent
                                payments within 30 day
                              </li>
                              <li>
                                New subscribers payments will be reported after
                                their subscriptions are approved
                              </li>
                            </ul>
                          </TooltipContent>
                        </Tooltip>
                      }
                    >
                      <span
                        className={cx(
                          "ml-auto block",
                          data.stats.verify === 0
                            ? "text-content-100"
                            : "text-content-300"
                        )}
                      >
                        {data.stats.verify}
                      </span>
                    </TimeLineItem>

                    <TimeLineItem
                      wrapperClassName="my-5 items-center"
                      childrenClassName="items-center flex w-full text-sm justify-between font-medium gap-2"
                      bullet={
                        <Tooltip>
                          <TooltipTrigger>
                            <Checkbox
                              checked={formData.update}
                              disabled={Boolean(data.stats.data_update === 0)}
                              onChange={(e) => {
                                setFormData((prev) => ({
                                  ...prev,
                                  update: e.target.checked
                                }));
                              }}
                              label="Update tradeline data"
                              wrapperClassName="!w-auto translate-x-[-19px]"
                            />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-[300px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-white">
                            <p className="font-bold">Update tradeline data</p>
                            <p>Critical tradeline data update</p>
                          </TooltipContent>
                        </Tooltip>
                      }
                    >
                      <span
                        className={cx(
                          "ml-auto block",
                          data.stats.data_update === 0
                            ? "text-content-100"
                            : "text-content-300"
                        )}
                      >
                        {data.stats.data_update}
                      </span>
                    </TimeLineItem>

                    <TimeLineItem
                      wrapperClassName="my-5 items-center"
                      childrenClassName="items-center flex w-full text-sm justify-between font-medium gap-2"
                      bullet={
                        <Tooltip>
                          <TooltipTrigger>
                            <Checkbox
                              checked={formData.moveout}
                              disabled={Boolean(data.stats.move_out === 0)}
                              onChange={(e) => {
                                setFormData((prev) => ({
                                  ...prev,
                                  moveout: e.target.checked
                                }));
                              }}
                              label="Process move out (close tradeline)"
                              wrapperClassName="!w-auto translate-x-[-19px]"
                            />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-[300px] rounded-lg z-[50] border bg-alpha-800 p-4 text-sm text-white">
                            <p className="font-bold">
                              Process move out (close tradeline)
                            </p>
                            <p>Close tradelines for renters</p>
                          </TooltipContent>
                        </Tooltip>
                      }
                    >
                      <span
                        className={cx(
                          "ml-auto block",
                          data.stats.move_out === 0
                            ? "text-content-100"
                            : "text-content-300"
                        )}
                      >
                        {data.stats.move_out}
                      </span>
                    </TimeLineItem>

                    {/* Add Update checkboxes. */}
                  </TimeLine>
                </div>
              </>
            ) : null}
          </div>
        </Card>
      </div>
      <Modal
        opened={cancelModal}
        onClose={() => {
          setCancelModal(false);
        }}
        panelClassName=" p-6 max-w-[366px] w-full"
      >
        <div className="grid place-items-center">
          <div className="grid w-min place-items-center rounded-full bg-danger-50 p-4 mb-4">
            <InfoCircle size="32px" className="stroke-danger-500" />
          </div>
        </div>
        <div className="flex items-center justify-between pb-4">
          <div className="text-center text-xl font-bold text-content-1000">
            Are you sure you want to cancel this process?{" "}
          </div>
        </div>
        <p className="text-sm text-content-500">
          You will lose all progress of analyzing the CSV file{" "}
        </p>
        <div className="mt-4 flex gap-2.5">
          <div className="w-full">
            <Button
              variant={ButtonVariant.BASE}
              onClick={() => setCancelModal(false)}
              className="w-full"
            >
              Continue
            </Button>
          </div>
          <div className="w-full">
            <Button
              className="w-full"
              variant={ButtonVariant.DANGER}
              onClick={() => {
                !isCancelPending && handleCancel();
              }}
              loading={isCancelPending}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </Layout>
  );
};
